import { ArrowRight } from '@carbon/icons-react';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router'
import { useMemo } from 'react';
import { z } from 'zod';

import { campaignGroupsQueryOptions } from '@/api/campaignGroups';
import { companyMembersQueryOptions } from '@/api/companyMembers';
import { formSummariesQueryOptions } from '@/api/formSummaries';
import { formTopicsQueryOptions } from '@/api/formTopics';
import { listCampaignsQueryOptions } from '@/api/listCampaigns';
import { listTopicsLegacyQueryOptions } from '@/api/listTopicsLegacy';
import { listTeamsQueryOptions } from '@/api/teams';
import { CampaignGroupsSection } from '@/components/admin/campaignDetails/campaignGroupsSection';
import { CampaignOverview } from '@/components/admin/campaignDetails/campaignOverview';
import {
  CampaignQuestionGroup,
  CampaignQuestionsSection,
} from '@/components/admin/campaignDetails/campaignQuestionsSection';
import { CampaignLockAction } from '@/components/admin/campaignLockAction';
import { StatusBadge } from '@/components/admin/statusBadge';
import { Breadcrumbs } from '@/components/breadcrumbs';
import { buttonVariants } from '@/components/input/button';
import { PENDING_CAMPAIGN_GROUPS, useCampaignGroups } from '@/hooks/useCampaignGroups';
import { Campaign, campaignStatusOf } from '@/schemas/campaign';
import { CampaignIdSchema } from '@/schemas/campaignId';
import { campaignFormIdsOf } from '@/util/campaignDetails';
import { membersAsOf } from '@/util/teamGroups';

// Campaign details (specs/014-campaign-details-view): overview, groups and questions, each with its
// own loading/error state. Read-only apart from open/close.
export const Route = createFileRoute('/dashboard/_authenticated/admin/campaigns/$campaignId')({
  component: RouteComponent,
  staticData: { pageWidth: 'wide' },
  params: {
    parse: (parameters) => z.object({ campaignId: z.preprocess(Number, CampaignIdSchema) }).parse(parameters)
  },
  errorComponent: ({ error }) => (
    <div className="text-body2 text-error-300">Invalid route params: {error.message}</div>
  ),
  // No breadcrumb of its own: the `campaigns` layout adds "Evaluations", the page appends the name.
})

function RouteComponent() {
  const { campaignId } = Route.useParams();
  const match = Route.useMatch();

  // From the list (shared cache with the cards, invalidated by open/close): `campaignDetailQueryOptions`
  // targets `api/admin/campaigns/{id}`, which the backend doesn't serve.
  const campaignsQuery = useQuery(listCampaignsQueryOptions());
  const campaign = campaignsQuery.data?.content.find((item) => item.id === campaignId);

  return (
    <div className="
      flex min-w-0 flex-col gap-6 pb-6
      lg:gap-8
    ">
      <CampaignLookup
        campaign={campaign}
        isError={campaignsQuery.isError}
        isPending={campaignsQuery.isPending}
        onRetry={() => void campaignsQuery.refetch()}
        pathname={match.pathname}
      />
    </div>
  );
}

function CampaignLookup({
  campaign,
  isPending,
  isError,
  onRetry,
  pathname,
}: {
  campaign: Campaign | undefined;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  pathname: string;
}) {
  if (isPending) {
    return (
      <div aria-label="Loading evaluation" className="flex flex-col gap-3" role="status">
        <div className="h-4 w-40 animate-pulse rounded-sm bg-primary-100" />
        <div className="
          h-8 w-64 max-w-full animate-pulse rounded-md bg-primary-100
        " />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-wrap items-center gap-4">
        <span className="text-body2 text-error-300">Couldn't load this evaluation.</span>
        <button
          className={buttonVariants({ size: 'sm', className: 'min-h-11 lg:min-h-0' })}
          onClick={onRetry}
          type="button"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col items-start gap-3">
        <div className="text-body2 text-neutral-600">Campaign not found.</div>
        <Link
          className="
            flex min-h-11 items-center text-body2 font-semibold
            text-primary-default
            hover:underline
            lg:min-h-0
          "
          to="/dashboard/admin/campaigns"
        >
          Back to evaluations
        </Link>
      </div>
    );
  }

  return (
    <>
      <Breadcrumbs crumbsToAppend={[{ label: campaign.name, path: pathname }]} />
      <CampaignDetails campaign={campaign} />
    </>
  );
}

function CampaignDetails({ campaign }: { campaign: Campaign }) {
  const queryClient = useQueryClient();
  const { data: membersPage } = useQuery(companyMembersQueryOptions());
  const { data: forms } = useQuery(formSummariesQueryOptions());
  const { data: teams } = useQuery(listTeamsQueryOptions());
  const teamsById = useMemo(() => (teams ? new Map(teams.map((team) => [team.id, team])) : undefined), [teams]);

  const campaigns = useMemo(() => [campaign], [campaign]);
  const groupsState = useCampaignGroups(campaigns, membersPage?.content, forms).get(campaign.id)
    ?? PENDING_CAMPAIGN_GROUPS;

  const formNamesById = useMemo(
    () => new Map((forms ?? []).map((form) => [form.id, form.name])),
    [forms],
  );

  const questionGroups = useCampaignQuestions(campaign, groupsState.status, campaignFormIdsOf(groupsState.groups), formNamesById);

  return (
    <>
      <div className="
        flex flex-col gap-3
        sm:flex-row sm:items-center sm:justify-between
      ">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <h1 className="
            min-w-0 text-page-title wrap-break-word text-primary-700
          ">{campaign.name}</h1>
          <StatusBadge status={campaign.status ?? campaignStatusOf(campaign)} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <CampaignLockAction campaign={campaign} />
          <Link
            className={buttonVariants({ size: 'sm', className: 'min-h-11 lg:min-h-0' })}
            search={{ q: campaign.name }}
            to="/dashboard/admin/reports"
          >
            View reports
            <ArrowRight aria-hidden size="16" />
          </Link>
        </div>
      </div>

      <div className="
        grid grid-cols-1 items-start gap-6
        lg:grid-cols-2
      ">
        <CampaignOverview campaign={campaign} />
        <CampaignGroupsSection
          formNamesById={formNamesById}
          membersAsOf={membersAsOf(campaign)}
          onRetry={() => void queryClient.refetchQueries({ queryKey: campaignGroupsQueryOptions(campaign.id).queryKey })}
          state={groupsState}
          teamsById={teamsById}
        />
      </div>

      <CampaignQuestionsSection groups={questionGroups} />
    </>
  );
}

/**
 * Questions grouped by form: the forms named by the campaign's groups, each from
 * `api/admin/forms/{id}/topics`. A campaign whose groups name no forms (created before groups)
 * falls back to the campaign's flat list, which has no form names.
 */
function useCampaignQuestions(
  campaign: Campaign,
  groupsStatus: 'pending' | 'error' | 'success',
  formIds: ReturnType<typeof campaignFormIdsOf>,
  formNamesById: Map<ReturnType<typeof campaignFormIdsOf>[number], string>,
): CampaignQuestionGroup[] {
  const useFallback = groupsStatus !== 'pending' && formIds.length === 0;

  const formTopics = useQueries({
    queries: formIds.map((formId) => formTopicsQueryOptions(formId)),
  });
  const campaignTopics = useQuery({
    ...listTopicsLegacyQueryOptions(campaign.id),
    enabled: useFallback,
  });

  if (groupsStatus === 'pending') {
    return [{ formName: 'Questions', status: 'pending', topics: [], retry: () => {} }];
  }

  if (useFallback) {
    return [{
      formName: 'Questions',
      status: campaignTopics.status,
      topics: campaignTopics.data ?? [],
      retry: () => void campaignTopics.refetch(),
    }];
  }

  return formIds.map((formId, index) => {
    const query = formTopics[index];
    return {
      formId,
      formName: formNamesById.get(formId) ?? `Form #${formId}`,
      status: query?.status ?? 'pending',
      topics: query?.data ?? [],
      retry: () => void query?.refetch(),
    };
  });
}
